import { useState } from "react";
import { useList, useCreate, useUpdate, useRemove } from "@/hooks/crud";
import { Modal } from "@/components/Modal";
import { TextField } from "@/components/Fields";
import { Spinner, ErrorBanner, Empty } from "@/components/Feedback";
import { formatNumber, slugify } from "@/lib/format";
import type { Artist } from "@/types/database";

function ArtistForm({ artist, onClose }: { artist: Artist | null; onClose: () => void }) {
  const create = useCreate("artists");
  const update = useUpdate("artists");
  const [name, setName] = useState(artist?.name ?? "");
  const [slug, setSlug] = useState(artist?.slug ?? "");
  const [url, setUrl] = useState(artist?.soundcloud_url ?? "");
  const [followers, setFollowers] = useState(
    artist?.followers_count != null ? String(artist.followers_count) : "",
  );
  const mutation = artist ? update : create;

  async function save() {
    if (!name.trim()) return;
    const body = {
      name: name.trim(),
      slug: (slug.trim() || slugify(name)).trim(),
      soundcloud_url: url.trim() || null,
      followers_count: followers.trim() ? Number(followers) : null,
    };
    if (artist) await update.mutateAsync({ id: artist.id, body });
    else await create.mutateAsync(body);
    onClose();
  }

  return (
    <Modal
      title={artist ? "Edit Artist" : "New Artist"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={mutation.isPending || !name.trim()}>
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {mutation.error && <ErrorBanner error={mutation.error} />}
      <TextField label="Name *" value={name} onChange={setName} />
      <TextField label="Slug" value={slug} onChange={setSlug} placeholder={slugify(name) || "auto"} />
      <TextField label="SoundCloud URL" value={url} onChange={setUrl} />
      <TextField label="Followers" value={followers} onChange={setFollowers} type="number" />
    </Modal>
  );
}

export default function ArtistsList() {
  const { data, isLoading, error } = useList("artists", "order=name");
  const remove = useRemove("artists");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Artist | null>(null);
  const artists = (data?.rows ?? []) as Artist[];

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Artists</h1>
          <div className="page-sub">{data ? `${data.total} total` : "—"}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          + New Artist
        </button>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading ? (
        <Spinner />
      ) : artists.length === 0 ? (
        <Empty>No artists yet.</Empty>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th className="num">Followers</th>
                <th>SoundCloud</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {artists.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{a.name}</td>
                  <td className="muted mono">{a.slug}</td>
                  <td className="num">{formatNumber(a.followers_count)}</td>
                  <td className="muted">
                    {a.soundcloud_url ? (
                      <a href={a.soundcloud_url} target="_blank" rel="noreferrer">
                        open
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="btn btn-sm btn-ghost" onClick={() => setEditing(a)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          if (confirm(`Delete ${a.name}? This cascades to their tracks.`))
                            remove.mutate(a.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <ArtistForm artist={null} onClose={() => setCreating(false)} />}
      {editing && <ArtistForm artist={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
